import React, { useState, useEffect } from 'react';
import { RefreshCw, Search } from 'lucide-react';
import { toast } from 'react-toastify';
import { formatCurrency, formatNumber } from '../../utils/currency';

// const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://check.policypulsehq.com/api';

export function EbayProductList({ storeId }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState('');
  const [editingCost, setEditingCost] = useState(null);
  const [costValue, setCostValue] = useState('');

  useEffect(() => {
    if (storeId) {
      fetchProducts();
    }
  }, [storeId]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_BASE_URL}/ebay/${storeId}/products`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setProducts(data.products);
      } else {
        toast.error('Failed to load products');
      }
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_BASE_URL}/ebay/${storeId}/products/sync`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(`Synced ${data.synced} products!`);
        fetchProducts();
      } else {
        toast.error('Sync failed');
      }
    } catch (error) {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleUpdateCost = async (productId) => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_BASE_URL}/ebay/${storeId}/products/${productId}/cost`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ cost_price: parseFloat(costValue) })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Cost price updated!');
        setEditingCost(null);
        fetchProducts();
      } else {
        toast.error('Failed to update cost');
      }
    } catch (error) {
      toast.error('Failed to update cost');
    }
  };

  const filteredProducts = products.filter(p =>
    p.title?.toLowerCase().includes(search.toLowerCase()) ||
    p.sku?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">eBay Products</h1>
          <p className="text-gray-600">{products.length} total products</p>
        </div>
        <button
          onClick={handleSync}
          disabled={syncing}
          className="flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-lg disabled:opacity-50"
        >
          <RefreshCw className={`mr-2 ${syncing ? 'animate-spin' : ''}`} size={20} />
          {syncing ? 'Syncing...' : 'Sync from eBay'}
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-3 text-gray-400" size={20} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by product name or SKU..."
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b-2 border-gray-200">
              <tr>
                <th className="text-left py-4 px-6 text-gray-600 font-semibold">Product</th>
                <th className="text-left py-4 px-6 text-gray-600 font-semibold">SKU</th>
                <th className="text-left py-4 px-6 text-gray-600 font-semibold">Condition</th>
                <th className="text-right py-4 px-6 text-gray-600 font-semibold">Price</th>
                <th className="text-right py-4 px-6 text-gray-600 font-semibold">Cost</th>
                <th className="text-right py-4 px-6 text-gray-600 font-semibold">Stock</th>
                <th className="text-right py-4 px-6 text-gray-600 font-semibold">Margin</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((product) => (
                <tr key={product.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                  <td className="py-4 px-6">
                    <div>
                      <p className="font-medium text-gray-800">{product.title}</p>
                    </div>
                  </td>
                  <td className="py-4 px-6 text-gray-700">{product.sku || 'N/A'}</td>
                  <td className="py-4 px-6">
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                      {product.condition_type || 'NEW'}
                    </span>
                  </td>
                  <td className="py-4 px-6 text-right font-semibold text-blue-600">
                    {formatCurrency(product.price)}
                  </td>
                  <td className="py-4 px-6 text-right">
                    {editingCost === product.id ? (
                      <input
                        type="number"
                        step="0.01"
                        value={costValue}
                        onChange={(e) => setCostValue(e.target.value)}
                        onBlur={() => handleUpdateCost(product.id)}
                        onKeyPress={(e) => e.key === 'Enter' && handleUpdateCost(product.id)}
                        className="w-24 px-2 py-1 border border-blue-500 rounded text-right focus:ring-2 focus:ring-blue-500"
                        autoFocus
                      />
                    ) : (
                      <button
                        onClick={() => {
                          setEditingCost(product.id);
                          setCostValue(product.cost_price || '0');
                        }}
                        className="text-gray-700 hover:text-blue-600 font-medium"
                      >
                        {formatCurrency(product.cost_price || 0)}
                      </button>
                    )}
                  </td>
                  <td className="py-4 px-6 text-right">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      product.inventory_quantity === 0 ? 'bg-red-100 text-red-700' :
                      product.inventory_quantity <= 10 ? 'bg-yellow-100 text-yellow-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {product.inventory_quantity}
                    </span>
                  </td>
                  <td className="py-4 px-6 text-right font-semibold text-green-600">
                    {formatCurrency((parseFloat(product.price) - parseFloat(product.cost_price || 0)))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default EbayProductList;